import { useState } from "react";
import { Mail, Copy, Check } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/constants";

export function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Button variant="outline" size="sm" className="shrink-0" onClick={copy} aria-label={label}>
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      <span className="ml-1">{copied ? "Copied" : "Copy"}</span>
    </Button>
  );
}

export function GmailConfirmButton({ href }) {
  return (
    <Button asChild variant="outline" size="sm" className="shrink-0">
      <a href={href} target="_blank" rel="noopener noreferrer">
        Confirm Gmail forwarding
      </a>
    </Button>
  );
}

/**
 * Tenant forwarding address + onboarding status. Props come from
 * /api/client/me: intake_email, intake_first_email_at, gmail_forward_link.
 * Renders nothing when there is no address.
 */
export default function ForwardingAddressCard({ address, firstEmailAt, gmailLink }) {
  if (!address) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5" style={{ color: BRAND.text }} />
          Your forwarding address
        </CardTitle>
        <CardDescription>Forward your portal login codes (2FA) here.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <code
            data-testid="forwarding-address"
            className="flex-1 min-w-0 truncate rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm"
          >
            {address}
          </code>
          <CopyButton value={address} label="Copy address" />
        </div>

        {firstEmailAt ? (
          <p className="text-sm font-medium text-green-600">✓ First email received</p>
        ) : (
          <p className="text-sm text-gray-500">Waiting for your first email</p>
        )}

        {gmailLink && (
          <div
            className="rounded-lg px-4 py-3 flex items-center gap-3"
            style={{ backgroundColor: BRAND.soft }}
          >
            <p className="text-sm text-gray-900 flex-1">Using Gmail? Confirm forwarding:</p>
            <GmailConfirmButton href={gmailLink} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
